import {useMemo} from "react";
import {EmployeeRef, SeatingPlanDeskAssignment, SeatingPlanMove} from "../../../api/Model";
import {useSeatingPlanStore} from "../../../api/SeatingPlanStore";
import {useFormDialogAction} from "../../../components/form-dialog/FormDialogProvider";
import {RemoteValue} from "../../../util/RemoteValue";
import {
    createDeskNeighbourhoodMismatchFixForm,
    DeskNeighbourhoodMismatchFix,
    DeskNeighbourhoodMismatchFixForm,
} from "./DeskNeighbourhoodMismatchFixForm";
import type {DeskNeighbourhoodMismatchProblem} from "./DeskNeighbourhoodMismatchWidget";
import {
    asDeskAssignment,
    asEmployeeMoves,
    exceptionalMessageHandler,
    useCreateAndOpenSeatingPlan
} from "../useFixDeskAssignments";
import {groupBy} from "../../../util/MapUtil";


export function useFixDeskNeighbourhoodMismatches(
    problems: RemoteValue<DeskNeighbourhoodMismatchProblem[]>,
) {
    const {setDeskAssignment, setEmployeeMoves} = useSeatingPlanStore();
    const createAndOpenSeatingPlan = useCreateAndOpenSeatingPlan(exceptionalMessageHandler);

    const openFixDialog = useFormDialogAction({
        title: 'Fix neighbourhood mismatches',
        description: 'Choose which side of each mismatch should be updated. A seating plan will be created so you can review and apply the proposed changes.',
        submitButtonText: 'Create seating plan',
        fullWidth: true,
        renderForm: DeskNeighbourhoodMismatchFixForm,
        action: async openDialog => {
            const currentProblems = await problems.value;
            openDialog({
                initialValue: createDeskNeighbourhoodMismatchFixForm(currentProblems),
                onSubmit: async form => {
                    if (form.every(item => item.solution.type === 'ignore')) {
                        throw new Error('Choose Move Desk or Move Employee for at least one mismatch.');
                    }

                    await createAndOpenSeatingPlan?.(
                        {
                            name: 'Fix desk and neighbourhood mismatches',
                            description: 'Proposed fixes for desk and employee neighbourhood mismatches.',
                        },
                        async plan => {
                            const employeeMoves: Map<EmployeeRef['id'], SeatingPlanMove[]> = groupBy(form
                                .filter((fix): fix is DeskNeighbourhoodMismatchFix & {solution: {type: 'move-employee'}} => fix.solution.type === 'move-employee')
                                .flatMap(fix => asEmployeeMoves(
                                    fix.problem.desk.assignedTo,
                                    fix.solution.fromNeighbourhoods,
                                    fix.solution.toNeighbourhoods,
                                )), move => move.employee.id);

                            const deskMoves = form
                                .filter((fix): fix is DeskNeighbourhoodMismatchFix & {solution: {type: 'move-desk'}} => fix.solution.type === 'move-desk')
                                .map<SeatingPlanDeskAssignment>(fix =>
                                    asDeskAssignment({...fix.problem.desk, neighbourhoodId: fix.solution.toNeighbourhood?.id})
                                );

                            for (const [_, moves] of employeeMoves) {
                                await setEmployeeMoves(plan, moves[0].employee, moves);
                            }
                            for (const deskAssignment of deskMoves) {
                                await setDeskAssignment(plan, deskAssignment.desk, deskAssignment);
                            }
                        }
                    )

                },
                successMessage: 'Created a seating plan with the proposed fixes.',
                errorMessage: 'Failed to create the seating plan. Please try again later.',
            });
        },
    }, [problems, createAndOpenSeatingPlan, setDeskAssignment, setEmployeeMoves]);

    return useMemo(() => createAndOpenSeatingPlan ? openFixDialog : undefined, [createAndOpenSeatingPlan, openFixDialog]);
}
